import { formatMonth } from "@erp/i18n/format";
import { PURCHASE_GROUP_VALUES, SALES_CHANNEL_VALUES, SALES_GROUP_VALUES } from "@erp/shared";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { BarChart3, Download, Sheet } from "lucide-react";
import { useCallback, type ReactNode } from "react";
import { z } from "zod";
import { Button } from "~/components/ui/button";
import { Field } from "~/components/ui/field";
import { Input } from "~/components/ui/input";
import { Page, PageHeader, Panel, Segmented } from "~/components/ui/page";
import { Select } from "~/components/ui/select";
import { EmptyState, ErrorState, Skeleton } from "~/components/ui/states";
import { ChannelBar } from "~/features/reports/channel-bar";
import { RankedBars } from "~/features/reports/charts";
import { ReconciliationReport } from "~/features/reports/reconciliation";
import { PERIOD_PRESETS, presetRange, type PeriodPreset } from "~/features/reports/periods";
import { MonthlyChart } from "~/features/reports/report-charts";
import { api, unwrap } from "~/lib/api";
import { cn } from "~/lib/cn";
import { csvAmount, downloadCsv } from "~/lib/csv";
import { downloadXlsx, type XlsxCell } from "~/lib/xlsx-export";
import { useFormat, useLocale, useT } from "~/lib/i18n";

const isoDate = z.iso.date();
const search = z.object({
  tab: z.enum(["sales", "purchases", "reconciliation"]).optional(),
  period: z.enum(PERIOD_PRESETS).optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
  group: z.enum([...SALES_GROUP_VALUES, ...PURCHASE_GROUP_VALUES]).optional(),
  channel: z.enum(SALES_CHANNEL_VALUES).optional(),
});
type Search = z.infer<typeof search>;

export const Route = createFileRoute("/_app/reports/")({ validateSearch: search, component: Reports });

const ALL = "__all";

interface Column<R> {
  id: string;
  header: string;
  value: (r: R) => ReactNode;
  total?: ReactNode;
  csv: (r: R) => string | number | null;
  /** Typed value for the Excel export (numbers stay numbers); the CSV value otherwise. */
  xlsx?: (r: R) => XlsxCell;
  align?: "end";
  primary?: boolean;
}

function Reports() {
  const t = useT();
  const navigate = useNavigate({ from: Route.fullPath });
  const filters = Route.useSearch();
  const tab = filters.tab ?? "sales";
  const period = filters.period ?? "month";
  const range = period === "custom" && filters.from && filters.to ? { from: filters.from, to: filters.to } : presetRange(period);
  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const groups = tab === "sales" ? SALES_GROUP_VALUES : PURCHASE_GROUP_VALUES;
  const group = filters.group && (groups as readonly string[]).includes(filters.group) ? filters.group : groups[0];
  const invalid = range.from > range.to;

  return (
    <Page>
      <PageHeader title={t("reports.title")} />
      <div className="mb-4 w-fit max-w-full">
        <Segmented
          label={t("reports.title")}
          value={tab}
          onChange={(v) => setSearch({ tab: v === "sales" ? undefined : v, group: undefined, channel: undefined })}
          options={[
            { value: "sales", label: t("reports.tabs.sales") },
            { value: "purchases", label: t("reports.tabs.purchases") },
            { value: "reconciliation", label: t("reports.tabs.reconciliation") },
          ]}
        />
      </div>
      <Panel>
        <div className="grid grid-cols-2 gap-3 border-b border-line px-4 py-4 md:px-5 lg:flex lg:flex-wrap lg:items-end">
          <Field label={t("reports.presets.label")} className="max-sm:col-span-2 lg:w-44">
            <Select
              value={period}
              onValueChange={(v) => {
                const next = v as PeriodPreset;
                setSearch(
                  next === "custom"
                    ? { period: next, ...range }
                    : { period: next === "month" ? undefined : next, from: undefined, to: undefined },
                );
              }}
              options={PERIOD_PRESETS.map((p) => ({ value: p, label: t(`reports.presets.${p}`) }))}
            />
          </Field>
          <Field label={t("reports.from")} className="lg:w-40" error={invalid ? t("reports.invalidRange") : undefined}>
            <Input
              type="date"
              value={range.from}
              onChange={(e) => e.target.value && setSearch({ period: "custom", from: e.target.value, to: range.to })}
            />
          </Field>
          <Field label={t("reports.to")} className="lg:w-40">
            <Input
              type="date"
              value={range.to}
              onChange={(e) => e.target.value && setSearch({ period: "custom", from: range.from, to: e.target.value })}
            />
          </Field>
          {tab === "reconciliation" ? null : (
            <Field label={t("reports.groupBy")} className="max-sm:col-span-2 lg:w-44">
              <Select
                value={group}
                onValueChange={(v) => setSearch({ group: v })}
                options={groups.map((g) => ({ value: g, label: t(`reports.groups.${g}`) }))}
              />
            </Field>
          )}
          {tab === "sales" ? (
            <Field label={t("reports.channel")} className="max-sm:col-span-2 lg:w-48">
              <Select
                value={filters.channel ?? ALL}
                onValueChange={(v) => setSearch({ channel: v === ALL ? undefined : (v as Search["channel"]) })}
                options={[
                  { value: ALL, label: t("reports.allChannels") },
                  ...SALES_CHANNEL_VALUES.map((c) => ({ value: c, label: t(`reports.channels.${c}`) })),
                ]}
              />
            </Field>
          ) : null}
        </div>
        {invalid ? null : tab === "reconciliation" ? (
          <ReconciliationReport from={range.from} to={range.to} />
        ) : tab === "sales" ? (
          <SalesReport
            from={range.from}
            to={range.to}
            group={group as (typeof SALES_GROUP_VALUES)[number]}
            channel={filters.channel}
          />
        ) : (
          <PurchaseReport from={range.from} to={range.to} group={group as (typeof PURCHASE_GROUP_VALUES)[number]} />
        )}
      </Panel>
    </Page>
  );
}

function useGroupLabel() {
  const t = useT();
  const [locale] = useLocale();
  return (group: string, label: string) =>
    group === "month"
      ? formatMonth(label, locale, "long")
      : group === "channel"
        ? t(`reports.channels.${label as (typeof SALES_CHANNEL_VALUES)[number]}`)
        : label;
}

function SalesReport({
  from,
  to,
  group,
  channel,
}: {
  from: string;
  to: string;
  group: (typeof SALES_GROUP_VALUES)[number];
  channel?: Search["channel"];
}) {
  const t = useT();
  const format = useFormat();
  const [locale] = useLocale();
  const groupLabel = useGroupLabel();
  const query = useQuery({
    queryKey: ["reports", "sales", from, to, group, channel],
    queryFn: () => unwrap(api.reports.sales.get({ query: { from, to, groupBy: group, channel } })),
    placeholderData: keepPreviousData,
  });
  type Row = NonNullable<typeof query.data>["items"][number];
  const totals = query.data?.totals;
  // Revenue whose cost is unknown (archives, articles without a purchase cost) has no margin: rates are on the rest.
  const rate = (margin: number, net: number) => (net !== 0 ? format.percent(margin / net) : "");
  const erpNet = (r: { netCents: number; uncostedNetCents: number }) => r.netCents - r.uncostedNetCents;
  const legacyOnly = (r: Row) => r.uncostedNetCents !== 0 && erpNet(r) === 0;
  const columns: Column<Row>[] = [
    {
      id: "label",
      header: t(`reports.groups.${group}`),
      primary: true,
      value: (r) => (
        <>
          <span className="block">{groupLabel(group, r.label)}</span>
          {r.detail ? <span className="block text-xs text-fg-3 tabular">{r.detail}</span> : null}
        </>
      ),
      total: t("reports.total"),
      csv: (r) => groupLabel(group, r.label),
    },
    {
      id: "qty",
      header: t("reports.columns.quantity"),
      align: "end",
      value: (r) => format.number(r.quantity),
      total: totals && format.number(totals.quantity),
      csv: (r) => r.quantity,
    },
    {
      id: "net",
      header: t("reports.columns.net"),
      align: "end",
      value: (r) => format.money(r.netCents),
      total: totals && format.money(totals.netCents),
      csv: (r) => csvAmount(r.netCents, locale),
      xlsx: (r) => r.netCents / 100,
    },
    {
      id: "cost",
      header: t("reports.columns.cost"),
      align: "end",
      value: (r) => (legacyOnly(r) ? "—" : format.money(r.costCents)),
      total: totals && format.money(totals.costCents),
      csv: (r) => csvAmount(r.costCents, locale),
      xlsx: (r) => r.costCents / 100,
    },
    {
      id: "margin",
      header: t("reports.columns.margin"),
      align: "end",
      value: (r) => (legacyOnly(r) ? "—" : format.money(r.marginCents)),
      total: totals && format.money(totals.marginCents),
      csv: (r) => csvAmount(r.marginCents, locale),
      xlsx: (r) => r.marginCents / 100,
    },
    {
      id: "rate",
      header: t("reports.columns.marginRate"),
      align: "end",
      value: (r) => rate(r.marginCents, erpNet(r)),
      total: totals && rate(totals.marginCents, erpNet(totals)),
      csv: (r) => (erpNet(r) ? (r.marginCents / erpNet(r)).toFixed(4) : null),
      xlsx: (r) => (erpNet(r) ? Number((r.marginCents / erpNet(r)).toFixed(4)) : null),
    },
  ];
  return (
    <ReportTable
      query={query}
      columns={columns}
      rowKey={(r) => r.key}
      note={t("reports.salesNote")}
      filename={`${t("reports.fileSales")}-${from}-${to}.csv`}
      chart={(rows) =>
        group === "month" ? (
          <MonthlyChart
            rows={rows.map((r) => ({
              label: r.label,
              net: r.netCents,
              margin: r.marginCents,
              rate: erpNet(r) ? r.marginCents / erpNet(r) : 0,
            }))}
            series={[
              { key: "net", label: t("reports.columns.net"), color: "var(--chart-current)", kind: "bar" },
              { key: "margin", label: t("reports.columns.margin"), color: "var(--chart-margin)", kind: "bar" },
              { key: "rate", label: t("reports.columns.marginRate"), color: "var(--chart-quote)", kind: "line", ratio: true },
            ]}
          />
        ) : group === "channel" ? (
          <ChannelBar
            slices={rows.map((r) => ({
              key: r.key,
              label: groupLabel(group, r.label),
              cents: r.netCents,
              color: `var(--chart-${r.label})`,
            }))}
          />
        ) : (
          <div className="flex flex-col gap-3">
            <p className="text-xs text-fg-3">{t("reports.chartTop")}</p>
            <RankedBars
              total={Math.max(1, totals?.netCents ?? 0)}
              rows={rows.slice(0, 10).map((r) => ({
                key: r.key,
                label: groupLabel(group, r.label),
                detail: r.detail,
                value: Math.max(0, r.netCents),
                valueLabel: format.money(r.netCents),
              }))}
            />
          </div>
        )
      }
    />
  );
}

function PurchaseReport({ from, to, group }: { from: string; to: string; group: (typeof PURCHASE_GROUP_VALUES)[number] }) {
  const t = useT();
  const format = useFormat();
  const [locale] = useLocale();
  const groupLabel = useGroupLabel();
  const query = useQuery({
    queryKey: ["reports", "purchases", from, to, group],
    queryFn: () => unwrap(api.reports.purchases.get({ query: { from, to, groupBy: group } })),
    placeholderData: keepPreviousData,
  });
  type Row = NonNullable<typeof query.data>["items"][number];
  const totals = query.data?.totals;
  const columns: Column<Row>[] = [
    {
      id: "label",
      header: t(`reports.groups.${group}`),
      primary: true,
      value: (r) => groupLabel(group, r.label),
      total: t("reports.total"),
      csv: (r) => groupLabel(group, r.label),
    },
    {
      id: "orders",
      header: t("reports.columns.orders"),
      align: "end",
      value: (r) => format.number(r.orders),
      total: totals && format.number(totals.orders),
      csv: (r) => r.orders,
    },
    {
      id: "ordered",
      header: t("reports.columns.ordered"),
      align: "end",
      value: (r) => format.money(r.orderedCents),
      total: totals && format.money(totals.orderedCents),
      csv: (r) => csvAmount(r.orderedCents, locale),
      xlsx: (r) => r.orderedCents / 100,
    },
    {
      id: "received",
      header: t("reports.columns.received"),
      align: "end",
      value: (r) => format.money(r.receivedCents),
      total: totals && format.money(totals.receivedCents),
      csv: (r) => csvAmount(r.receivedCents, locale),
      xlsx: (r) => r.receivedCents / 100,
    },
    {
      id: "paid",
      header: t("reports.columns.paid"),
      align: "end",
      value: (r) => format.money(r.paidCents),
      total: totals && format.money(totals.paidCents),
      csv: (r) => csvAmount(r.paidCents, locale),
      xlsx: (r) => r.paidCents / 100,
    },
  ];
  return (
    <ReportTable
      query={query}
      columns={columns}
      rowKey={(r) => r.key}
      note={t("reports.purchasesNote")}
      filename={`${t("reports.filePurchases")}-${from}-${to}.csv`}
      chart={(rows) =>
        group === "month" ? (
          <MonthlyChart
            rows={rows.map((r) => ({ label: r.label, ordered: r.orderedCents, received: r.receivedCents }))}
            series={[
              { key: "ordered", label: t("reports.columns.ordered"), color: "var(--chart-current)", kind: "bar" },
              { key: "received", label: t("reports.columns.received"), color: "var(--chart-margin)", kind: "bar" },
            ]}
          />
        ) : (
          <div className="flex flex-col gap-3">
            <p className="text-xs text-fg-3">{t("reports.chartTop")}</p>
            <RankedBars
              total={Math.max(1, totals?.orderedCents ?? 0)}
              rows={rows.slice(0, 10).map((r) => ({
                key: r.key,
                label: groupLabel(group, r.label),
                value: Math.max(0, r.orderedCents),
                valueLabel: format.money(r.orderedCents),
              }))}
            />
          </div>
        )
      }
    />
  );
}

function ReportTable<R>({
  query,
  columns,
  rowKey,
  note,
  filename,
  chart,
}: {
  chart?: (rows: R[]) => ReactNode;
  query: {
    data?: { items: R[] };
    isPending: boolean;
    isError: boolean;
    error: unknown;
    isPlaceholderData: boolean;
    refetch: () => unknown;
  };
  columns: Column<R>[];
  rowKey: (r: R) => string;
  note: string;
  filename: string;
}) {
  const t = useT();
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  if (query.isPending)
    return (
      <div className="flex flex-col gap-2 p-5">
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="h-8 w-full" />
        ))}
      </div>
    );
  const rows = query.data?.items ?? [];
  if (rows.length === 0) return <EmptyState icon={<BarChart3 />} title={t("reports.emptyTitle")} body={t("reports.emptyBody")} />;
  const [primary, ...rest] = columns;
  return (
    <div className={cn("transition-opacity", query.isPlaceholderData && "opacity-60")}>
      {chart ? <div className="border-b border-line px-4 py-5 md:px-5">{chart(rows)}</div> : null}
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 md:px-5">
        <p className="max-w-2xl text-xs text-fg-3">{note}</p>
        <div className="flex gap-2">
          <Button
            onClick={() =>
              downloadCsv(
                filename,
                columns.map((c) => c.header),
                rows.map((r) => columns.map((c) => c.csv(r))),
              )
            }
          >
            <Download />
            {t("reports.export")}
          </Button>
          <Button
            onClick={() =>
              downloadXlsx(
                filename.replace(/\.csv$/, ".xlsx"),
                filename.replace(/\.csv$/, ""),
                columns.map((c) => c.header),
                rows.map((r) => columns.map((c) => (c.xlsx ?? c.csv)(r))),
              )
            }
          >
            <Sheet />
            {t("reports.exportXlsx")}
          </Button>
        </div>
      </div>

      <table className="w-full text-sm max-md:hidden">
        <thead>
          <tr className="border-y border-line text-[12.5px] text-fg-3">
            {columns.map((c) => (
              <th
                key={c.id}
                className={cn("h-9 px-3 font-medium first:pl-5 last:pr-5", c.align === "end" ? "text-right" : "text-left")}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={rowKey(r)} className="border-b border-line hover:bg-surface-2">
              {columns.map((c) => (
                <td key={c.id} className={cn("px-3 py-2 first:pl-5 last:pr-5", c.align === "end" && "text-right tabular")}>
                  {c.value(r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-surface-2 font-medium">
            {columns.map((c) => (
              <td key={c.id} className={cn("h-10 px-3 first:pl-5 last:pr-5", c.align === "end" && "text-right tabular")}>
                {c.total}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>

      <ul className="border-t border-line md:hidden">
        {rows.map((r) => (
          <li key={rowKey(r)} className="border-b border-line px-4 py-3">
            <div className="mb-1.5 text-sm font-medium">{primary?.value(r)}</div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              {rest.map((c) => (
                <div key={c.id} className="flex justify-between gap-2">
                  <dt className="text-fg-3">{c.header}</dt>
                  <dd className="tabular">{c.value(r)}</dd>
                </div>
              ))}
            </dl>
          </li>
        ))}
        <li className="bg-surface-2 px-4 py-3">
          <p className="mb-1.5 text-sm font-medium">{primary?.total}</p>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            {rest.map((c) => (
              <div key={c.id} className="flex justify-between gap-2">
                <dt className="text-fg-3">{c.header}</dt>
                <dd className="font-medium tabular">{c.total}</dd>
              </div>
            ))}
          </dl>
        </li>
      </ul>
    </div>
  );
}
